import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

import DatafyLogo from '~/shared/assets/icons/datafy-logo.svg';
import DatafyLogoSm from '~/shared/assets/icons/datafy-logo-sm.svg';
import HelpIcon from '~/shared/assets/icons/carbon-help.svg';
import Typography from '~/shared/components/Typography';
import Tag from '~/shared/components/Tag';
import { getDocsSiteURL } from '~/shared/utils/docsSiteURL';

import { sidebarItems, SidebarItemType } from './common';
import { SIDEBAR_BREAKPOINT_WIDTH } from './sidebarConstants';

export default function SidebarCollapsible({
  isCollapsed,
  setIsCollapsed,
  windowWidth,
}: {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  windowWidth: number;
}) {
  const [helpTextVisible, setHelpTextVisible] = useState(!isCollapsed);

  useEffect(() => {
    if (isCollapsed) {
      setHelpTextVisible(false);
      return;
    }

    const timeoutId = window.setTimeout(() => setHelpTextVisible(true), 400);
    return () => window.clearTimeout(timeoutId);
  }, [isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const sidebarWidthClass = isCollapsed
    ? 'w-[88px] min-w-[88px] px-[20px]'
    : windowWidth <= SIDEBAR_BREAKPOINT_WIDTH
      ? 'w-[220px] min-w-[220px] px-[20px]'
      : 'w-[240px] min-w-[240px] px-[20px]';

  return (
    <div
      className={clsx(
        'bg-bg',
        'pt-[20px] 4xl:pt-[33px]',
        'pb-[16px]',
        'flex',
        'flex-col',
        'transition-all',
        'duration-300',
        'fixed',
        'top-0',
        'left-0',
        'h-full',
        'z-40',
        'border-r',
        'border-r-primary-70',
        sidebarWidthClass
      )}
    >
      <div className="flex-1 flex-col  flex">
        <div className={' relative flex h-fit mb-9'}>
          <div className="bgred">
            <DatafyLogoSm
              className={clsx(
                'h-6 transition-opacity duration-300 ml-6 absolut left-1/2 -translate-x-1/2',
                isCollapsed ? 'opacity-100 ' : 'opacity-0  delay-300'
              )}
            />
          </div>
          <DatafyLogo
            className={clsx(
              'h-6 absolute -ml-4',
              isCollapsed
                ? 'opacity-0 transition-opacity duration-300 '
                : 'opacity-100 transition-opacity duration-300 delay-300'
            )}
          />
          <button
            onClick={toggleCollapse}
            className="w-fit mlauto -mr-8 absolute -top2.5 xl: top-[-2.5px] 4xl:top-0 right-0 size-6 aspect-square cursor-pointer grid place-items-center rounded-[3px] border border-primary-70 bg-white z-[100] focus-outline-none  "
          >
            <ChevronRightIcon
              className={clsx(
                'w-4 h-4 stroke-gray-2 stroke-3 transition-all duration-300 group-hover:stroke-primary-500',
                {
                  'rotate-180': !isCollapsed,
                }
              )}
            />
          </button>
        </div>

        <div className="flex flex-col gap-[20px] px2.5 ">
          {sidebarItems.map((item, i) => (
            <Fragment key={`${item.pathname}_${i}`}>
              <SidebarItem
                {...{
                  ...item,
                  isCollapsed,
                  icon: {
                    ...item.icon,
                    colorMode: item.icon.colorMode || 'fill',
                  },
                }}
              />
              {item.pathname === '/optimization' ? (
                <div className="w-full h-[1px] bg-primary-70"></div>
              ) : null}
            </Fragment>
          ))}
        </div>
      </div>

      <a
        href={getDocsSiteURL()}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open documentation (opens in new tab)"
        className={clsx(
          'h-[44px] px-[13px] gap-[8px] items-center rounded-[8px] flex group relative mt-auto cursor-pointer w-fit',
          'bg-transparent text-primary-900 border-0 hover:text-primary-500 hover:bg-primary-20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400'
        )}
      >
        <HelpIcon
          className={clsx(
            'w-[20px] aspect-square',
            'text-primary-800 group-hover:text-primary-500'
          )}
        />
        {!isCollapsed && helpTextVisible && (
          <Typography
            variant="p3"
            className={clsx(
              'transition-opacity font-family-outfit delay-300',
              'group-hover:text-primary-800 delay-0',
              !helpTextVisible
                ? 'opacity-0 w-0 overflow-hidden delay-200 transition-opacity duration-300'
                : 'opacity-100 delay-200 transition-opacity duration-300'
            )}
            style={{ opacity: helpTextVisible ? 1 : 0 }}
          >
            Help
          </Typography>
        )}
      </a>
    </div>
  );
}

function SidebarItem({
  pathname: itemPathname,
  title,
  icon: { el: Icon, colorMode },
  isDisabed,
  isBeta,
  isCollapsed,
  subItems,
}: SidebarItemType & { isCollapsed: boolean }) {
  const { pathname } = useLocation();
  const isActive = pathname.startsWith(itemPathname);
  const [textVisible, setTextVisible] = useState(!isCollapsed);
  const hasSubItems = subItems && subItems.length > 0;
  const isDisclosureOpen = hasSubItems && pathname.startsWith(itemPathname);
  const [isDisclosureExpanded, setIsDisclosureExpanded] = useState(isDisclosureOpen);

  useEffect(() => {
    if (!isCollapsed) {
      setTimeout(() => setTextVisible(true), 400); // Small delay
    } else {
      setTextVisible(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (isDisclosureOpen) {
      setIsDisclosureExpanded(true);
    }
  }, [isDisclosureOpen]);

  const handleMainItemClick = (e: React.MouseEvent) => {
    if (hasSubItems && !isCollapsed) {
      e.preventDefault();
      setIsDisclosureExpanded(!isDisclosureExpanded);
    }
  };

  const isMainItemActive = isActive && (!hasSubItems || isCollapsed);
  const isMainItemWithSubItemsActive = isActive && hasSubItems && !isCollapsed;

  const closeRef = useRef<(() => void) | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const triggerClassName = clsx(
    'h-[44px]',
    'gap-[8px]',
    'items-center',
    'rounded-[8px]',
    'flex',
    'group',
    'relative px-[13px]',
    isCollapsed ? 'w-full mx-auto' : 'px-1',
    isMainItemActive
      ? 'bg-primary-500 hover:bg-primary-500'
      : isMainItemWithSubItemsActive
        ? 'bg-primary-50 hover:bg-primary-50'
        : 'hover:bg-primary-50',
    { 'opacity-30 pointer-events-none': isDisabed }
  );

  if (isCollapsed && hasSubItems) {
    return (
      <div className="flex flex-col">
        <Popover className="relative flex flex-col">
          {({ open, close }) => {
            closeRef.current = close;
            return (
              <>
                <div
                  className="flex flex-col"
                  onMouseEnter={() => {
                    if (closeTimeoutRef.current) {
                      clearTimeout(closeTimeoutRef.current);
                      closeTimeoutRef.current = null;
                    }
                    if (!open) {
                      triggerRef.current?.click();
                    }
                  }}
                  onMouseLeave={() => {
                    closeTimeoutRef.current = setTimeout(() => {
                      closeRef.current?.();
                      closeTimeoutRef.current = null;
                    }, 150);
                  }}
                >
                  <PopoverButton
                    ref={triggerRef}
                    as="div"
                    className={clsx(triggerClassName, open && !isMainItemActive && 'bg-primary-50')}
                  >
                    <div className="flex items-center justify-center size10">
                      <Icon
                        className={clsx(
                          'w-[20px]',
                          'aspect-square',
                          isMainItemActive
                            ? colorMode === 'fill'
                              ? 'fill-white'
                              : 'stroke-white'
                            : open && !isMainItemActive
                              ? colorMode === 'fill'
                                ? 'fill-primary-500'
                                : 'stroke-primary-500'
                              : colorMode === 'fill'
                                ? 'fill-primary-800 group-hover:fill-primary-500'
                                : 'stroke-primary-800 group-hover:stroke-primary-500'
                        )}
                      />
                    </div>
                  </PopoverButton>
                </div>
                <PopoverPanel
                  anchor="right start"
                  transition
                  className="flex flex-col  min-w-[220px] rounded-[8px] bg-bg  shadow-[0px_4px_9.8px_0px_#0000000D] border border-primary-70 [--anchor-gap:7px] transition duration-200 ease-out data-closed:opacity-0 data-closed:scale-95 z-[10050]"
                >
                  <div
                    className="flex flex-col"
                    onMouseEnter={() => {
                      if (closeTimeoutRef.current) {
                        clearTimeout(closeTimeoutRef.current);
                        closeTimeoutRef.current = null;
                      }
                    }}
                    onMouseLeave={() => {
                      closeTimeoutRef.current = setTimeout(() => {
                        closeRef.current?.();
                        closeTimeoutRef.current = null;
                      }, 150);
                    }}
                  >
                    {subItems!.map((subItem) => {
                      const isSubActive =
                        pathname === subItem.pathname ||
                        pathname.startsWith(`${subItem.pathname}/`);
                      return (
                        <div key={subItem.pathname} className="p-1">
                          <Link
                            to={subItem.pathname}
                            className={clsx(
                              'h-[44px] gap-[8px] items-center rounded-[8px] flex px-4 font-family-outfit',
                              isSubActive
                                ? 'bg-primary-500 text-white hover:bg-primary-500'
                                : 'hover:bg-primary-50 text-primary-800'
                            )}
                          >
                            <Typography
                              variant="product-2"
                              className="flex-1 font-family-outfit"
                              color={isSubActive ? 'text-white' : undefined}
                            >
                              {subItem.title}
                            </Typography>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </PopoverPanel>
              </>
            );
          }}
        </Popover>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <Link
        to={hasSubItems && !isCollapsed ? '#' : itemPathname}
        onClick={handleMainItemClick}
        className={clsx(
          'h-[44px] ',
          'gap-[8px]',
          'items-center',
          'rounded-[8px]',
          'flex',
          'group',
          'relative px-[13px]',
          isCollapsed ? '  w-full mx-auto' : 'px-1',
          isMainItemActive
            ? 'bg-primary-500 hover:bg-primary-500'
            : isMainItemWithSubItemsActive
              ? 'bg-primary-50 hover:bg-primary-50'
              : 'hover:bg-primary-50',
          {
            'opacity-30 pointer-events-none': isDisabed,
          }
        )}
      >
        <div className="relative flex items-center justify-center">
          <Icon
            className={clsx(
              'w-[20px]',
              'aspect-square',
              isActive && hasSubItems && !isCollapsed
                ? colorMode === 'fill'
                  ? 'fill-primary-500'
                  : 'stroke-primary-500'
                : isMainItemActive
                  ? colorMode === 'fill'
                    ? 'fill-white'
                    : 'stroke-white'
                  : colorMode === 'fill'
                    ? 'fill-primary-800 group-hover:fill-primary-500'
                    : 'stroke-primary-800 group-hover:stroke-primary-500'
            )}
          />
          {isBeta && isCollapsed && (
            <span className="absolute top-[2.5px] right-[1px] size-[7px] rounded-full bg-ocean-blue border-[1px] border-bg" />
          )}
        </div>
        {!isCollapsed && textVisible && (
          <>
            <Typography
              variant="p3"
              className={clsx(
                'transition-opacity font-family-outfit delay-300',
                !isMainItemActive ? 'group-hover:text-primary-800  delay-0' : undefined,
                !textVisible
                  ? 'opacity-0 w-0 overflow-hidden delay-200 transition-opacity duration-300 '
                  : 'opacity-100 delay-200 transition-opacity duration-300 '
              )}
              color={isMainItemActive ? 'text-white' : undefined}
              style={{ opacity: textVisible ? 1 : 0 }}
            >
              {title}
            </Typography>
            {isBeta && <Tag variant="beta" size="status" text="Beta" />}
            {hasSubItems && (
              <ChevronDownIcon
                className={clsx(
                  'w-4 h-4 transition-transform duration-200 ease-in-out ml-auto',
                  isMainItemActive
                    ? 'stroke-white'
                    : 'stroke-primary-800 group-hover:stroke-primary-500',
                  isDisclosureExpanded ? 'rotate-180' : ''
                )}
              />
            )}
          </>
        )}
      </Link>
      {hasSubItems && !isCollapsed && isDisclosureExpanded && textVisible && (
        <div className="flex flex-col gap-1 mt-1">
          {subItems.map((subItem) => {
            const isSubActive =
              pathname === subItem.pathname || pathname.startsWith(`${subItem.pathname}/`);
            return (
              <Link
                key={subItem.pathname}
                to={subItem.pathname}
                className={clsx(
                  'h-[44px]',
                  'gap-[8px]',
                  'items-center',
                  'rounded-[8px]',
                  'flex',
                  'group',
                  'relative px-[13px]',
                  'pl-[40px]',
                  isSubActive ? 'bg-primary-500 hover:bg-primary-500' : 'hover:bg-primary-50'
                )}
              >
                <Typography
                  variant="p3"
                  className={clsx(
                    'transition-opacity delay-300 flex-1 font-family-outfit',
                    !isSubActive ? 'group-hover:text-primary-800 delay-0' : undefined,
                    !textVisible
                      ? 'opacity-0 w-0 overflow-hidden delay-200 transition-opacity duration-300 '
                      : 'opacity-100 delay-200 transition-opacity duration-300 '
                  )}
                  color={isSubActive ? 'text-white' : undefined}
                  style={{ opacity: textVisible ? 1 : 0 }}
                >
                  {subItem.title}
                </Typography>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
